import { HeroSection } from "./sections/HeroSection";
import {
    FaqSection, RichTextSection, JournalSection, EbooksSection, BlogHighlightSection,
    BenefitsSection, ConsultationCtaSection, FormulationSection, LifestyleBandSection,
    MarqueeSection, PatentedSection, SocialWallSection, TestimonialsSection,
    ImageBannerSection, ScienceFormulationSection, ConsultExperienceSection, BlogIndexSection,
} from "./sections/Sections";
import {
    ComparisonSection, ConceptFlowSection, DualCardsSection, ProductScienceHubSection,
    ScienceDisclaimerSection, ScienceHeroSection,
} from "./sections/ScienceSections";
import { ProductPairing } from "./sections/ProductPairing";
import { PlainCards } from "./sections/PlainCards";
import { ShopByConcern } from "./sections/ShopByConcern";

const t = (key, label, type = "text", extra = {}) => ({ key, label, type, ...extra });

export const REGISTRY = {
    hero: {
        label: "Hero", component: HeroSection, description: "Headline, buttons and the featured product card",
        fields: [
            t("eyebrow", "Eyebrow"),
            t("title_top_prefix", "Title line 1"), t("title_top_accent", "Title line 1 highlight"),
            t("title_bottom_prefix", "Title line 2"), t("title_bottom_accent", "Title line 2 highlight"),
            t("body", "Body", "textarea"), t("tagline", "Tagline"),
            t("primary_label", "Primary button"), t("primary_href", "Primary link", "link"),
            t("secondary_label", "Secondary button"), t("secondary_href", "Secondary link", "link"),
            t("badges", "Badges", "badges"),
            t("title_size", "Headline size", "select",
                { options: ["", "small", "medium", "large", "extra large"] }),
            t("title_color", "Headline text colour", "color"),
            t("title_top_accent_color", "Line 1 highlight colour", "color"),
            t("title_bottom_accent_color", "Line 2 highlight colour", "color"),
            t("product_slug", "Featured product", "product"),
            t("show_product_card", "Show product card", "bool"),
            t("media_type", "Featured media", "select", { options: ["none", "image", "video"] }),
            t("media_frame", "Image style", "select",
                { options: ["boxed card", "clean (no frame)"] }),
            t("media_layout", "Where the media sits", "select",
                { options: ["beside the text", "behind the text (full width)"] }),
            t("media_url", "Image or video", "media"),
            t("media_url_mobile", "Mobile image (optional)", "media"),
            t("media_motion", "Motion", "select",
                { options: ["none", "slow zoom in", "slow zoom out", "slow drift", "moves as you scroll"] }),
            t("media_motion_seconds", "Motion length (seconds)", "number"),
            t("media_scrim", "Text backdrop strength", "select", { options: ["soft", "medium", "strong"] }),
            t("media_focus", "Focus point on desktop", "text"),
            t("media_focus_mobile", "Focus point on mobile", "text"),
            t("logo_ring", "Animated logo ring", "select",
                { options: ["none", "right side"] }),
            t("logo_ring_glyph", "Logo centre image (the Re mark)", "media"),
            t("logo_ring_size", "Ring size", "select",
                { options: ["matches heading height", "small", "medium", "large", "extra large"] }),
            t("logo_ring_seconds", "Ring spin speed (seconds per turn)", "number"),
            t("logo_ring_nudge_x", "Ring nudge left/right (%)", "number"),
            t("logo_ring_nudge_y", "Ring nudge up/down (%)", "number"),
            t("logo_ring_mobile", "Ring on mobile", "select",
                { options: ["below the text", "hidden"] }),
            t("media_alt", "Media description (alt text)"),
            t("media_caption", "Caption under the media"),
            t("media_href", "Media link", "link"),
        ],
    },
    marquee: {
        label: "Scrolling band", component: MarqueeSection, description: "Looping strip of short phrases",
        fields: [
            t("items", "Phrases & icons", "phrases"),
            t("icon", "Default separator icon", "band_icon"),
            t("icon_hex", "Icon colour", "color"),
            t("icon_size", "Icon size (px)", "number"),
            t("bg_hex", "Band background", "color"),
            t("text_hex", "Text colour", "color"),
            t("href", "Band link (whole band clickable)", "link"),
            t("tilt", "Band angle", "select", { options: ["straight", "angled"] }),
            t("tilt_angle", "How many degrees", "number"),
            t("tilt_seconds", "Scroll speed (seconds per loop)", "number"),
            t("thickness_preset", "Band thickness preset", "select",
                { options: ["auto", "slim", "normal", "bold"] }),
            t("band_height", "Band height — desktop (px)", "px"),
            t("band_height_mobile", "Band height — mobile (px)", "px"),
            t("band_pad", "Vertical padding — desktop (px)", "px"),
            t("band_pad_mobile", "Vertical padding — mobile (px)", "px"),
            t("font_size", "Text size — desktop (px)", "px"),
            t("font_size_mobile", "Text size — mobile (px)", "px"),
            t("font_weight", "Text weight", "select",
                { options: ["auto", "400", "500", "600", "700", "800", "900"] }),
        ],
        defaults: {
            tilt: "straight", tilt_angle: -3, tilt_seconds: 34, icon: "", icon_size: 22,
            thickness_preset: "auto", font_weight: "auto",
        },
    },
    benefits: {
        label: "Feature cards", component: BenefitsSection, description: "Heading with icon cards",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("note", "Side note", "textarea"),
            t("cards", "Cards", "cards", {
                item: [t("icon", "Icon", "icon"), t("title", "Title"), t("desc", "Description", "textarea")],
            }),
        ],
    },
    formulation: {
        label: "Formulation & actives", component: FormulationSection,
        description: "Live actives and combos pulled from the product CRM",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("product_slug", "Product", "product"),
        ],
    },
    lifestyle_band: {
        label: "Full-width banner", component: LifestyleBandSection, description: "Image band with heading and buy button",
        fields: [
            t("image_url", "Background image", "image"), t("image_alt", "Image alt text"),
            t("eyebrow", "Eyebrow"), t("heading_prefix", "Heading"), t("heading_accent", "Heading highlight"),
            t("heading_suffix", "Heading end"), t("cta_label", "Button label"),
            t("product_slug", "Product", "product"),
        ],
    },
    patented: {
        label: "Highlight cards", component: PatentedSection, description: "Two-up cards with badge, text and link",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"),
            t("cards", "Cards", "cards", {
                item: [t("tag", "Badge"), t("title", "Title"), t("desc", "Description", "textarea"),
                    t("link", "Link", "link"), t("link_label", "Link label"),
                    t("color", "Accent", "select", { options: ["lime", "gold"] })],
            }),
        ],
    },
    consultation_cta: {
        label: "Consultation CTA", component: ConsultationCtaSection, description: "Prompt visitors to book a consultation",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("cta_label", "Button label"), t("cta_href", "Button link", "link"),
        ],
    },
    testimonials: {
        label: "Testimonials", component: TestimonialsSection, description: "Approved customer testimonials",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"),
            t("product_slug", "Product", "product"), t("limit", "How many", "number"),
        ],
    },
    rich_text: {
        label: "Heading & text", component: RichTextSection, description: "Editorial heading, paragraph and optional button",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("align", "Alignment", "select", { options: ["left", "center"] }),
            t("cta_label", "Button label"), t("cta_href", "Button link", "link"),
        ],
    },
    faq: {
        label: "FAQ", component: FaqSection, description: "Expandable question and answer list",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"),
            t("items", "Questions", "cards", {
                item: [t("question", "Question"), t("answer", "Answer", "textarea")],
            }),
        ],
    },
    ebooks: {
        label: "Ebooks & guides strip", component: EbooksSection,
        description: "Digital guides pulled automatically from the shop",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("sub", "Sub-line", "textarea"),
            t("limit", "How many", "number"), t("cta_label", "Link label"),
        ],
    },
    journal: {
        label: "Journal / blog strip", component: JournalSection,
        description: "Latest published articles, pulled automatically from the Blog CMS",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"),
            t("source", "Which articles", "select", { options: ["settings", "latest", "featured", "category"] }),
            t("category_slug", "Category address (for category)"),
            t("limit", "How many", "number"), t("cta_label", "Link label"),
        ],
    },
    blog_highlight: {
        label: "From the blog (highlight)", component: BlogHighlightSection,
        description: "One article with a highlighted passage that links to the full read",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"),
            t("highlight", "Article & highlight", "blog_highlight"),
            t("cta_label", "Link label"),
            t("layout", "Layout", "select", { options: ["side-by-side", "quote"] }),
        ],
    },
    image_banner: {
        label: "Image banner with heading", component: ImageBannerSection,
        description: "Full-width photo banner — used at the top of pages like Science",
        fields: [
            t("image_url", "Banner image", "media"), t("image_alt", "Image alt text"),
            t("eyebrow", "Eyebrow"), t("heading", "Heading"), t("heading_accent", "Heading highlight"),
            t("body", "Body", "textarea"),
            t("height", "Height", "select", { options: ["short", "medium", "tall"] }),
        ],
    },
    science_formulation: {
        label: "Formulation deep dive", component: ScienceFormulationSection,
        description: "Combos, actives and the explorer — live from the product CRM",
        fields: [t("product_slug", "Product", "product")],
    },
    consult_experience: {
        label: "Consultation experience", component: ConsultExperienceSection,
        description: "The live consultation page — doctors, areas, packs and booking",
        fields: [],
    },
    blog_index: {
        label: "Blog listing", component: BlogIndexSection,
        description: "Search, categories and every published article",
        fields: [t("heading", "Heading"), t("sub", "Sub-line", "textarea")],
    },
    science_hero: {
        label: "Science hero (3 pillars)", component: ScienceHeroSection,
        description: "The Science page opener with Unani / Nutrition / Ayurveda pillars",
        fields: [
            t("eyebrow", "Eyebrow"), t("title", "Title"), t("title_accent", "Title highlight"),
            t("sub", "Sub-line", "textarea"), t("body", "Body", "textarea"),
            {
                key: "pillars", label: "Pillars", type: "cards",
                item: [t("tag", "Tag"), t("title", "Title"), t("desc", "Description", "textarea"),
                    t("badge", "Badge (leave empty for none)"),
                    t("accent", "Accent colour", "select", { options: ["gold", "blue", "green", "neutral"] }),
                    t("emphasis", "Emphasised", "bool")],
            },
        ],
    },
    dual_cards: {
        label: "Concept cards (traditional + modern)", component: DualCardsSection,
        description: "Cards that pair a traditional concept with a modern perspective",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("left_label", "Left label"), t("right_label", "Right label"),
            t("columns", "Columns", "select", { options: ["2", "3"] }),
            t("accent", "Accent colour", "select", { options: ["gold", "blue", "green", "neutral"] }),
            t("right_accent", "Right label colour", "select", { options: ["", "gold", "blue", "green"] }),
            {
                key: "cards", label: "Cards", type: "cards",
                item: [t("tag", "Tag"), t("title", "Title"), t("traditional", "Traditional concept", "textarea"),
                    t("modern", "Modern perspective", "textarea"), t("desc", "Extra note", "textarea"),
                    t("accent", "Card accent (optional)", "select", { options: ["", "gold", "blue", "green", "neutral"] })],
            },
        ],
    },
    concept_flow: {
        label: "Numbered flow", component: ConceptFlowSection,
        description: "A step-by-step flow such as Traditional knowledge → Formulation",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("accent", "Accent colour", "select", { options: ["gold", "blue", "green", "neutral"] }),
            {
                key: "steps", label: "Steps", type: "cards",
                item: [t("number", "Number"), t("title", "Title"), t("desc", "Description", "textarea")],
            },
        ],
    },
    comparison_table: {
        label: "Two-column comparison", component: ComparisonSection,
        description: "Unani perspective beside the modern nutritional perspective",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("left_label", "Left column label"), t("right_label", "Right column label"),
            t("accent", "Left column colour", "select", { options: ["gold", "blue", "green", "neutral"] }),
            t("right_accent", "Right column colour", "select", { options: ["blue", "gold", "green", "neutral"] }),
            t("note", "Footnote", "textarea"),
            { key: "rows", label: "Rows", type: "cards", item: [t("left", "Left"), t("right", "Right")] },
        ],
    },
    product_science_hub: {
        label: "Product science hub", component: ProductScienceHubSection,
        description: "Product cards from CRM → Science, in the order set there",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("sub", "Sub-line", "textarea"),
            t("limit", "How many to show (0 = all)", "number"),
        ],
    },
    science_disclaimer: {
        label: "Science disclaimer", component: ScienceDisclaimerSection,
        description: "Educational disclaimer — falls back to CRM → Science → Settings",
        fields: [t("text", "Disclaimer", "textarea")],
    },
    plain_cards: {
        label: "Plain cards", component: PlainCards,
        description: "Simple editable cards — text, image, colour, link",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("columns", "Columns", "select", { options: ["2", "3", "4"] }),
            t("accent", "Accent colour", "select", { options: ["gold", "blue", "green", "lime", "neutral", "custom"] }),
            t("accent_hex", "Custom colour (hex)"),
            {
                key: "cards", label: "Cards", type: "cards",
                item: [t("tag", "Tag"), t("title", "Title"), t("desc", "Text", "textarea"),
                    t("image_url", "Image URL"), t("link", "Link"), t("link_label", "Link label"),
                    t("accent", "Card colour", "select", { options: ["", "gold", "blue", "green", "lime", "neutral", "custom"] }),
                    t("accent_hex", "Card custom colour (hex)")],
            },
        ],
    },
    product_pairing: {
        label: "Works best with", component: ProductPairing,
        description: "Cross-sell other RHM products — pairings or a full A+B+C combination",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("body", "Body", "textarea"),
            t("combination", "Combination line (e.g. ReDrive Max + ReHold Long + ReRise Up)"),
            t("accent", "Accent colour", "select", { options: ["gold", "blue", "green", "lime", "neutral", "custom"] }),
            t("accent_hex", "Custom colour (hex)"),
            {
                key: "items", label: "Products", type: "cards",
                item: [t("slug", "Product", "product"), t("tag", "Tag"), t("title", "Override title"),
                    t("desc", "Why it pairs", "textarea"), t("image_url", "Override image", "media")],
            },
        ],
    },
    shop_by_concern: {
        label: "Shop by concern", component: ShopByConcern,
        description: "Dark band with concern tabs — products come from the Concern tag in the product CRM",
        fields: [
            t("eyebrow", "Eyebrow"),
            t("heading_prefix", "Heading"), t("heading_accent", "Heading highlight"),
            t("body", "Sub-line", "textarea"),
            t("accent_hex", "Highlight colour", "color"),
            t("bg_hex", "Band colour", "color"), t("card_hex", "Card colour", "color"),
            t("columns", "Cards per row", "select", { options: ["2", "3", "4"] }),
            t("details_label", "Left button label"), t("cart_label", "Right button label"),
            t("empty_message", "Message when a tab has no products", "textarea"),
            {
                key: "tabs", label: "Tabs", type: "cards",
                item: [t("label", "Tab label"), t("concern", "Shows products tagged", "concern"),
                    t("limit", "How many products", "number")],
            },
        ],
        defaults: {
            eyebrow: "", heading_prefix: "Shop by", heading_accent: "Concern", body: "",
            accent_hex: "#A4E22B", bg_hex: "#141414", card_hex: "#FAFAF8", columns: "4",
            details_label: "Details", cart_label: "Add to Cart", empty_message: "",
            tabs: [
                { label: "Men's Vitality", concern: "Men's Vitality", limit: 8, visible: true },
                { label: "Liver", concern: "Liver", limit: 8, visible: true },
                { label: "Gut", concern: "Gut", limit: 8, visible: true },
                { label: "Skin", concern: "Skin", limit: 8, visible: true },
                { label: "Hair", concern: "Hair", limit: 8, visible: true },
            ],
        },
    },
    social_wall: {
        label: "Community wall", component: SocialWallSection, description: "Social posts grid",
        fields: [
            t("eyebrow", "Eyebrow"), t("heading", "Heading", "textarea"), t("note", "Side note", "textarea"),
            t("product_slug", "Product", "product"), t("limit", "How many", "number"),
            t("columns", "Columns", "number"),
        ],
    },
};

export const SECTION_TYPES = Object.entries(REGISTRY).map(([type, def]) => ({
    type, label: def.label, description: def.description,
}));

// Every section gets the same spacing controls, so staff can tighten or open up any gap on the page.
const SPACING_FIELDS = [
    t("space_top", "Space above", "spacing"),
    t("space_bottom", "Space below", "spacing"),
];
Object.values(REGISTRY).forEach((def) => {
    def.fields = [...(def.fields || []), ...SPACING_FIELDS];
});

export const blankSection = (type) => {
    const def = REGISTRY[type];
    const props = {};
    (def?.fields || []).forEach((f) => {
        if (f.type === "list") props[f.key] = ["Item one", "Item two"];
        else if (f.type === "phrases") props[f.key] = [{ text: "Powered by Nature" }, { text: "Backed by Science" }];
        else if (f.type === "badges") props[f.key] = [{ text: "Badge one" }, { text: "Badge two" }];
        else if (f.type === "blog_highlight") props[f.key] = { post_slug: "", text: "", show_title: true, show_image: true };
        else if (f.type === "media") props[f.key] = "";
        else if (f.type === "cards") props[f.key] = [{ title: "Card title", desc: "Short description", visible: true }];
        else if (f.type === "bool") props[f.key] = true;
        else if (f.type === "number") props[f.key] = 6;
        else if (f.type === "product") props[f.key] = "redrive-max";
        else props[f.key] = f.key === "heading" ? "New section heading" : "";
    });
    return {
        id: `section_${type}_${Math.random().toString(36).slice(2, 8)}`,
        type, name: def?.label || type, visible: true,
        props: { ...props, ...(def?.defaults || {}) },
    };
};
